import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Paper, Typography, Grid, TextField, Button, MenuItem, List, 
  ListItem, ListItemButton, ListItemText, ListItemIcon, Snackbar, 
  Alert, Divider, Chip, Checkbox, FormControlLabel, Avatar 
} from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import api from '../../api';
import SalaryDetails from './SalaryDetails'; 

const AddEmployee = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]); 
  const [eduLevels, setEduLevels] = useState([]);
  const [expRanges, setExpRanges] = useState([]);
  const [skillsMaster, setSkillsMaster] = useState([]);
  const [certsMaster, setCertsMaster] = useState([]);
  const [langsMaster, setLangsMaster] = useState([]);

  const [formData, setFormData] = useState({
    emp_id: '',
    first_name: '', last_name: '', email: '', mobile: '', dob: '', gender: '',
    father_name: '', mother_name: '', blood_group: '', address: '', location: '',
    official_email: '', date_of_joining: '', role: '', employee_level: '', 
    department_id: '', designation_id: '', leave_approver_l1: '', leave_approver_l2: '', 
    status: 'ACTIVE', shift_id: '', notice_period: '',
    education_id: '', experience_id: '', skills: [], certifications: [], languages: [],
    pan_number: '', aadhaar_number: '', uan_number: '', pf_number: '',
    bank_account_holder_name: '', bank_account: '', bank_name: '', branch_name: '', ifsc_code: '',
    ctc: '', basic: '', hra: '', special_allowance: '', gross: '', net_salary: '',
    epf_employer: '', epf_employee: '', esic_employer: '', esic_employee: '', gratuity_employer: '',
    tds: '', prof_tax: '',
    auto_basic_hra: false, auto_esic: false, auto_epf: false, auto_gratuity: false
  });

  useEffect(() => {
    Promise.all([
      api.get('/settings/departments').catch(() => ({ data: [] })),
      api.get('/settings/designations').catch(() => ({ data: [] })),
      api.get('/employees').catch(() => ({ data: [] })),
      api.get('/settings/education').catch(() => ({ data: [] })), 
      api.get('/settings/experience').catch(() => ({ data: [] })), 
      api.get('/settings/skills').catch(() => ({ data: [] })),
      api.get('/settings/certifications').catch(() => ({ data: [] })),
      api.get('/settings/languages').catch(() => ({ data: [] }))
    ]).then(responses => {
      setDepartments(responses[0].data || []);
      setDesignations(responses[1].data || []);
      setEmployees(responses[2].data || []);
      setEduLevels(responses[3].data || []);
      setExpRanges(responses[4].data || []);
      setSkillsMaster(responses[5].data || []);
      setCertsMaster(responses[6].data || []);
      setLangsMaster(responses[7].data || []);
    });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const renderMultiTags = (selected, placeholder) => {
    if (!Array.isArray(selected) || selected.length === 0) {
      return <Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>{placeholder}</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => <Chip key={value} label={value} size="small" color="primary" variant="outlined" />)}
      </Box>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };
    
    ['ctc', 'basic', 'hra', 'special_allowance', 'gross', 'epf_employer', 'epf_employee', 'esic_employer', 'esic_employee', 'gratuity_employer', 'tds', 'prof_tax', 'notice_period'].forEach(field => {
      payload[field] = (typeof payload[field] === 'string' && payload[field] !== '') ? parseFloat(payload[field]) : payload[field];
      if (payload[field] === '') payload[field] = 0;
    });

    delete payload.net_salary;
    
    if (!payload.emp_id || payload.emp_id.trim() === '') {
      delete payload.emp_id;
    }

    if (payload.shift_id === '') payload.shift_id = null;
    if (payload.department_id === '') payload.department_id = null;
    if (payload.designation_id === '') payload.designation_id = null;
    if (payload.education_id === '') payload.education_id = null;
    if (payload.experience_id === '') payload.experience_id = null;

    try {
      const res = await api.post('/employees', payload);
      const newEmpId = res.data.emp_id;

      if (sendWelcomeEmail) {
        try {
            const adminId = localStorage.getItem('emp_id') || '';
            const notifPayload = {
              employment_letter: false,
              welcome_email: true,
              manager_notification: false,
              it_notification: false,
              department_notification: false,
              company_notification: false,
              notice_period: 0
            };
            await api.post(`/employees/${newEmpId}/notifications?admin_id=${adminId}`, notifPayload);
            showMsg('Employee created & Welcome Email sent!', 'success');
        } catch (emailErr) {
            showMsg('Employee created, but failed to send welcome email.', 'warning');
        }
      } else {
        showMsg('Employee created successfully!', 'success');
      }

      setTimeout(() => navigate('/employees'), 1500);
      
    } catch (err) {
      let errorMsg = 'Failed to create employee';
      const detail = err.response?.data?.detail;
      
      if (Array.isArray(detail)) {
        errorMsg = detail.map(d => {
          const fieldName = d.loc && d.loc.length > 1 ? d.loc[d.loc.length - 1] : 'Field';
          const cleanField = String(fieldName).replace(/_/g, ' '); 
          return `${cleanField}: ${d.msg}`;
        }).join(' | ');
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (err.message) {
        errorMsg = err.message;
      }

      showMsg(errorMsg, 'error');
    }
  };

  const NavItem = ({ navId, label, icon }) => (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton selected={activeTab === navId} onClick={() => setActiveTab(navId)} sx={{ borderRadius: '8px', mx: 1, '&.Mui-selected': { bgcolor: 'primary.main', color: 'white' } }}>
        <ListItemIcon sx={{ minWidth: 40, color: activeTab === navId ? 'white' : 'inherit' }}>{icon}</ListItemIcon>
        <ListItemText primary={label} primaryTypographyProps={{ fontWeight: activeTab === navId ? 'bold' : 'medium' }} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '85vh', mt: 2, gap: 3 }}>
      <Paper elevation={3} sx={{ width: 260, flexShrink: 0, borderRadius: 3, overflow: 'hidden', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate('/employees')}>Back</Button>
        </Box>
        <Divider />
        
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa', position: 'relative' }}>
          <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 1, bgcolor: 'primary.main', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            NEW
          </Avatar>
          <Typography variant="subtitle1" fontWeight="bold">New Employee</Typography>
          <Typography variant="caption" color="textSecondary">Draft Profile</Typography>
        </Box>
        <Divider />
        
        <List component="nav" dense sx={{ flexGrow: 1 }}>
          <NavItem navId="personal" label="Personal Details" icon={<PersonIcon fontSize="small" />} />
          <NavItem navId="official" label="Official Information" icon={<WorkIcon fontSize="small" />} />
          <NavItem navId="education" label="Education & Skills" icon={<SchoolIcon fontSize="small" />} />
          <NavItem navId="account" label="Account Details" icon={<AccountBalanceIcon fontSize="small" />} />
          <NavItem navId="salary_details" label="Salary Details" icon={<AttachMoneyIcon fontSize="small" />} />
        </List>
        <Divider />
        
        <Box sx={{ p: 2, bgcolor: '#f0f4ff' }}>
          <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1, fontWeight: 'bold' }}>
            Onboarding Action
          </Typography>
          <FormControlLabel
            control={
              <Checkbox checked={sendWelcomeEmail} onChange={(e) => setSendWelcomeEmail(e.target.checked)} color="primary" size="small" />
            }
            label={<Typography variant="caption" sx={{ lineHeight: 1.2, display: 'block' }}>Send Welcome message & Login Credentials to Official Email</Typography>}
          />
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ flexGrow: 1, p: 4, borderRadius: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', textTransform: 'capitalize' }}>
              Add {activeTab.replace('_', ' ')}
            </Typography>
            <Button type="submit" variant="contained" color="success" startIcon={<SaveIcon />}>Save Employee</Button>
          </Box>
          <Divider sx={{ mb: 4 }} />

          <Box sx={{ maxWidth: 800 }}>
            {activeTab === 'personal' && (
              <Box>
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 'bold', mb: 2 }}>Basic Details</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}><TextField fullWidth required size="small" label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth required size="small" label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth required size="small" type="email" label="Personal Email Address" name="email" value={formData.email} onChange={handleChange} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth required size="small" label="Mobile Number" name="mobile" value={formData.mobile} onChange={handleChange} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth required size="small" type="date" label="Date of Birth" name="dob" InputLabelProps={{ shrink: true }} value={formData.dob} onChange={handleChange} /></Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select required fullWidth size="small" label="Gender" name="gender" value={formData.gender} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}>
                      <MenuItem value="" disabled><em>Select Gender</em></MenuItem>
                      <MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem><MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 'bold', mb: 2 }}>Family & Additional (Optional)</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Father's Name" name="father_name" value={formData.father_name} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Mother's Name" name="mother_name" value={formData.mother_name} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Blood Group" name="blood_group" value={formData.blood_group} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}>
                      <MenuItem value=""><em>Select Blood Group</em></MenuItem>
                      <MenuItem value="A+">A+</MenuItem><MenuItem value="A-">A-</MenuItem><MenuItem value="B+">B+</MenuItem><MenuItem value="B-">B-</MenuItem><MenuItem value="O+">O+</MenuItem><MenuItem value="O-">O-</MenuItem><MenuItem value="AB+">AB+</MenuItem><MenuItem value="AB-">AB-</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}><TextField fullWidth size="small" label="Current Location" name="location" value={formData.location} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12}><TextField fullWidth size="small" multiline rows={3} label="Full Address" name="address" value={formData.address} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 'official' && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      label="Employee ID (Optional)" 
                      name="emp_id" 
                      value={formData.emp_id} 
                      onChange={handleChange} 
                      placeholder="Leave empty to auto-generate" 
                      InputLabelProps={{ shrink: true }}
                      helperText="System will auto-assign if left blank"
                    />
                </Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth required size="small" type="email" label="Official Email (Login ID)" name="official_email" value={formData.official_email} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth required size="small" type="date" label="Date of Joining" name="date_of_joining" InputLabelProps={{ shrink: true }} value={formData.date_of_joining} onChange={handleChange} /></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select required fullWidth size="small" label="System Role" name="role" value={formData.role} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}>
                    <MenuItem value="" disabled><em>Select Role</em></MenuItem>
                    <MenuItem value="Employee">Employee</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select required fullWidth size="small" label="Employee Level" name="employee_level" value={formData.employee_level} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}>
                    <MenuItem value="" disabled><em>Select Level</em></MenuItem>
                    <MenuItem value="Level 1">Level 1</MenuItem>
                    <MenuItem value="Level 2">Level 2</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}><TextField select fullWidth size="small" label="Department" name="department_id" value={formData.department_id} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}><MenuItem value=""><em>Select Department</em></MenuItem>{departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12} sm={6}><TextField select fullWidth size="small" label="Designation" name="designation_id" value={formData.designation_id} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}><MenuItem value=""><em>Select Designation</em></MenuItem>{designations.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12} sm={6}><TextField select fullWidth size="small" label="L1 Manager" name="leave_approver_l1" value={formData.leave_approver_l1} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}><MenuItem value=""><em>None</em></MenuItem>{employees.map(e => <MenuItem key={e.emp_id} value={e.emp_id}>{e.first_name} {e.last_name} ({e.emp_id})</MenuItem>)}</TextField></Grid>
                <Grid item xs={12} sm={6}><TextField select fullWidth size="small" label="L2 Manager" name="leave_approver_l2" value={formData.leave_approver_l2} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}><MenuItem value=""><em>None</em></MenuItem>{employees.map(e => <MenuItem key={e.emp_id} value={e.emp_id}>{e.first_name} {e.last_name} ({e.emp_id})</MenuItem>)}</TextField></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Notice Period (Months)" name="notice_period" value={formData.notice_period} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
              </Grid>
            )}

            {activeTab === 'account' && (
              <Box>
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 'bold', mb: 2 }}>Statutory Numbers</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="PAN Number" name="pan_number" value={formData.pan_number} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Aadhaar Number" name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="UAN Number (PF)" name="uan_number" value={formData.uan_number} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="PF Number" name="pf_number" value={formData.pf_number} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />
                
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 'bold', mb: 2 }}>Bank Details</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Bank Name" name="bank_name" value={formData.bank_name} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Branch Name" name="branch_name" value={formData.branch_name} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Account Holder Name" name="bank_account_holder_name" value={formData.bank_account_holder_name} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Bank Account Number" name="bank_account" value={formData.bank_account} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="IFSC Code" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 'education' && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}><TextField select fullWidth size="small" label="Highest Education" name="education_id" value={formData.education_id} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}><MenuItem value=""><em>Select Education</em></MenuItem>{eduLevels.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12} sm={6}><TextField select fullWidth size="small" label="Experience Range" name="experience_id" value={formData.experience_id} onChange={handleChange} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ width: '100%' }}><MenuItem value=""><em>Select Experience</em></MenuItem>{expRanges.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12}><TextField select fullWidth size="small" label="Core Skills" name="skills" value={formData.skills} onChange={handleChange} InputLabelProps={{ shrink: true }} SelectProps={{ multiple: true, displayEmpty: true, renderValue: (selected) => renderMultiTags(selected, "Select Skills"), sx: { minHeight: 45, width: '100%' } }}><MenuItem value="" disabled><em>Select Skills</em></MenuItem>{skillsMaster.map(s => <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12}><TextField select fullWidth size="small" label="Certifications" name="certifications" value={formData.certifications} onChange={handleChange} InputLabelProps={{ shrink: true }} SelectProps={{ multiple: true, displayEmpty: true, renderValue: (selected) => renderMultiTags(selected, "Select Certifications"), sx: { minHeight: 45, width: '100%' } }}><MenuItem value="" disabled><em>Select Certifications</em></MenuItem>{certsMaster.map(c => <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12}><TextField select fullWidth size="small" label="Languages Known" name="languages" value={formData.languages} onChange={handleChange} InputLabelProps={{ shrink: true }} SelectProps={{ multiple: true, displayEmpty: true, renderValue: (selected) => renderMultiTags(selected, "Select Languages"), sx: { minHeight: 45, width: '100%' } }}><MenuItem value="" disabled><em>Select Languages</em></MenuItem>{langsMaster.map(l => <MenuItem key={l.name} value={l.name}>{l.name}</MenuItem>)}</TextField></Grid>
              </Grid>
            )}

            {activeTab === 'salary_details' && (
              <SalaryDetails formData={formData} handleChange={handleChange} setFormData={setFormData} />
            )}
          </Box>
        </Box>
      </Paper>
      
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddEmployee;